'use client'

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react'
import { getDeWiCatCollectables } from '@utils/solanaUtils'
import { init as initOrganization } from '@helium/organization-sdk'
import { init as initNftVoter } from '@helium/nft-voter-sdk'
import { init as initStateController } from '@helium/state-controller-sdk'
import { useAsync } from 'react-async-hook'
import { PublicKey, Transaction } from '@solana/web3.js'
import { bulkSendTransactions, chunks } from '@helium/spl-utils'
import { DaoProposal, ProposalMetadata } from 'types/solana'
import { Umi, createGenericFile } from '@metaplex-foundation/umi'
import { createUmi } from '@metaplex-foundation/umi-bundle-defaults'
import { ORGANIZATION, PROPOSAL_CONFIG } from '@utils/constants'
import { useSolana } from '@dewicats/connect-button'
import { usePathname } from 'next/navigation'
import NftVoter from '../../tasks/governance/nft_voter.json'

const GovernanceContext = createContext({})

type TransactionState = {
  loading: boolean
  error: Error | undefined
  signature: string | undefined
}

interface GovernanceState {
  transactionState: TransactionState
  vote: (proposalPubKey: PublicKey, choice: number) => Promise<void>
  clearTransaction: () => void
  setProposal: (
    proposal: DaoProposal,
    proposalMetadata: ProposalMetadata,
  ) => void
  setConfirmDraftProposal: (open: boolean) => void
  setVoteDialog: (open: boolean) => void
  setConfirmReviewProposal: (open: boolean) => void
  setPublishProposal: (open: boolean) => void
  publishProposal?: boolean
  voteDialog?: boolean
  proposal?: DaoProposal
  proposalMetadata?: ProposalMetadata
  confirmDraftProposal?: boolean
  reviewProposal?: boolean
  createProposal: (
    image: string,
    title: string,
    metadata: string,
  ) => Promise<void>
}

export type Action =
  | { type: 'update-transaction'; transactionState: TransactionState }
  | {
      type: 'set-proposal'
      proposal: DaoProposal | null
      proposalMetadata: ProposalMetadata
    }
  | {
      type: 'set-confirm-draft-proposal'
      confirmDraftProposal: boolean
    }
  | {
      type: 'set-confirm-review-proposal'
      reviewProposal: boolean
    }
  | {
      type: 'set-vote-dialog'
      setVoteDialog: boolean
    }
  | {
      type: 'set-publish-proposal'
      publishProposal: boolean
    }

export function governanceReducer(
  state: GovernanceState,
  action: Action,
): GovernanceState {
  switch (action.type) {
    case 'update-transaction': {
      return {
        ...state,
        transactionState: action.transactionState,
      }
    }
    case 'set-proposal':
      return {
        ...state,
        proposal: action.proposal,
        proposalMetadata: action.proposalMetadata,
      }
    case 'set-confirm-draft-proposal':
      return {
        ...state,
        confirmDraftProposal: action.confirmDraftProposal,
      }
    case 'set-confirm-review-proposal':
      return {
        ...state,
        reviewProposal: action.reviewProposal,
      }
    case 'set-vote-dialog':
      return {
        ...state,
        voteDialog: action.setVoteDialog,
      }
    case 'set-publish-proposal':
      return {
        ...state,
        publishProposal: action.publishProposal,
      }
    default:
      return state
  }
}

function GovernanceProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(governanceReducer, {
    transactionState: {
      loading: false,
      error: undefined,
      signature: undefined,
    },
    vote: (() => {}) as any,
    clearTransaction: () => {},
    setProposal: () => {},
    setConfirmDraftProposal: () => {},
    setConfirmReviewProposal: () => {},
    setPublishProposal: () => {},
    setVoteDialog: () => {},
    createProposal: (image: string, title: string, metadata: string) =>
      Promise.resolve(),
  })

  const { anchorProvider } = useSolana()
  const pathname = usePathname()

  const umi = useMemo(
    () => createUmi(process.env.NEXT_PUBLIC_REACT_APP_SOLANA_RPC_HOST),
    [],
  )

  // This provider sits in the root layout, but each of these helpers fetches an
  // IDL over RPC on mount. Only the DAO and treasury routes ever vote or draft
  // proposals, so everywhere else (/, /cat/[n], /explorer/*) skips the calls.
  const needsGovernance =
    pathname?.startsWith('/dewihall') || pathname?.startsWith('/treasury')

  const { result: organizationSdk } = useAsync(async () => {
    if (!needsGovernance) return undefined
    return initOrganization(anchorProvider)
  }, [anchorProvider, needsGovernance])

  const { result: nftVoterSdk } = useAsync(async () => {
    if (!needsGovernance) return undefined
    return initNftVoter(
      anchorProvider,
      new PublicKey('nftvLQ5t6xe2nQF1NBmBBmn15ed59tU6vSCkwQNEqdc'),
      NftVoter as any,
    )
  }, [anchorProvider, needsGovernance])

  const { result: stateControllerSdk } = useAsync(async () => {
    if (!needsGovernance) return undefined
    return initStateController(anchorProvider)
  }, [anchorProvider, needsGovernance])

  const vote = useCallback(
    async (proposalPubKey: PublicKey, choice: number) => {
      const dewiCats = await getDeWiCatCollectables(anchorProvider)
      let sig = undefined

      try {
        if (!vote || !anchorProvider || !nftVoterSdk || dewiCats.length === 0)
          return

        const instructions = await Promise.all(
          dewiCats.map(async (dewicat) => {
            const instruction = await nftVoterSdk.methods
              .voteV0({
                choice,
              })
              .accountsPartial({
                proposal: proposalPubKey,
                mint: new PublicKey(dewicat.id),
                nftVoter: new PublicKey(process.env.NEXT_PUBLIC_NFT_VOTER),
              })
              .instruction()
            return {
              ixs: instruction,
              dewicat,
            }
          }),
        )

        const txs = chunks(instructions, 4).map((chunk) => {
          const tx = new Transaction()
          tx.feePayer = anchorProvider.wallet.publicKey
          chunk.forEach(({ ixs }) => {
            tx.add(ixs)
            return tx
          })
          return tx
        })

        dispatch({
          type: 'update-transaction',
          transactionState: {
            loading: true,
            error: undefined,
            signature: undefined,
          },
        })

        const sigs = await bulkSendTransactions(anchorProvider, txs as never)
        sig = sigs[0]

        dispatch({
          type: 'update-transaction',
          transactionState: {
            loading: true,
            signature: sigs[0],
            error: undefined,
          },
        })
      } catch (e: any) {
        dispatch({
          type: 'update-transaction',
          transactionState: {
            loading: false,
            signature: sig,
            error: e,
          },
        })
      }
    },
    [nftVoterSdk, anchorProvider],
  )

  const clearTransaction = useCallback(() => {
    const clearedState: TransactionState = {
      loading: false,
      error: undefined,
      signature: undefined,
    }
    dispatch({ type: 'update-transaction', transactionState: clearedState })
  }, [])

  const setProposal = useCallback(
    (proposal: DaoProposal, proposalMetadata: ProposalMetadata) => {
      dispatch({ type: 'set-proposal', proposal, proposalMetadata })
    },
    [],
  )

  const setConfirmDraftProposal = useCallback((open: boolean) => {
    dispatch({ type: 'set-confirm-draft-proposal', confirmDraftProposal: open })
  }, [])

  const setConfirmReviewProposal = useCallback((open: boolean) => {
    dispatch({ type: 'set-confirm-review-proposal', reviewProposal: open })
  }, [])

  const setVoteDialog = useCallback((open: boolean) => {
    dispatch({ type: 'set-vote-dialog', setVoteDialog: open })
  }, [])

  const setPublishProposal = useCallback((open: boolean) => {
    dispatch({ type: 'set-publish-proposal', publishProposal: open })
  }, [])

  const createProposal = useCallback(
    async (image: string, title: string, metadata: string) => {
      const proposalMetadata = await createProposalMetadata(
        umi,
        image,
        title,
        metadata,
      )

      const {
        pubkeys: { proposal },
      } = await organizationSdk.methods
        .initializeProposalV0({
          maxChoicesPerVoter: 1,
          name: title,
          uri: proposalMetadata,
          choices: [
            {
              name: 'For',
              uri: null,
            },
            {
              name: 'Against',
              uri: null,
            },
            {
              name: 'Abstain',
              uri: null,
            },
          ],
          // TODO: Do we need tags to organize our proposals in groups?
          tags: ['test', 'tags'],
        })
        .accounts({
          organization: ORGANIZATION,
          proposalConfig: PROPOSAL_CONFIG,
          payer: anchorProvider.wallet.publicKey,
          owner: anchorProvider.wallet.publicKey,
        })
        .rpcAndKeys({ skipPreflight: true })

      // Wait 5 seconds to ensure the transaction is confirmed.
      // eslint-disable-next-line no-promise-executor-return
      await new Promise((resolve) => setTimeout(resolve, 15000))

      await stateControllerSdk.methods
        .updateStateV0({
          newState: {
            voting: {},
          },
        } as never)
        .accounts({ proposal })
        .rpcAndKeys({ skipPreflight: true })
    },
    [umi, organizationSdk, stateControllerSdk],
  )

  function base64ToArrayBuffer(base64: string) {
    var binaryString = atob(base64.split('base64,')[1])
    var bytes = new Uint8Array(binaryString.length)
    for (var i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    return bytes
  }

  const createProposalMetadata = async (
    umi: Umi,
    image: string,
    title: string,
    metadata: string,
  ) => {
    const genericFile = createGenericFile(
      base64ToArrayBuffer(image),
      `${title}.png`,
    )

    // Upload the asset.
    const [fileUri] = await umi.uploader.upload([genericFile], {
      onProgress(percent, ...args) {},
    })

    const proposalMetadata = {
      title,
      imageUri: fileUri,
      metadata,
    }

    // Upload the JSON metadata.
    const proposalMetadataUri = await umi.uploader.uploadJson(proposalMetadata)
    return proposalMetadataUri
  }

  const governance = useMemo(
    () => ({
      ...state,
      vote,
      clearTransaction,
      setProposal,
      setConfirmDraftProposal,
      setConfirmReviewProposal,
      setVoteDialog,
      setPublishProposal,
      createProposal,
    }),
    [
      state,
      vote,
      clearTransaction,
      setProposal,
      setConfirmDraftProposal,
      setConfirmReviewProposal,
      setVoteDialog,
      setPublishProposal,
      createProposal,
    ],
  )

  return (
    <GovernanceContext.Provider value={governance}>
      {children}
    </GovernanceContext.Provider>
  )
}

const useGovernance = (): GovernanceState => {
  return useContext(GovernanceContext) as GovernanceState
}

export { useGovernance, GovernanceProvider }
